'use client';

import React, { useEffect, useState } from 'react';
import { fetchStats } from '@/lib/supabase/services';
import type { AppStats } from '@/lib/supabase/services';

export default function KPIBentoGrid() {
  const [stats, setStats] = useState<AppStats | null>(null);

  useEffect(() => {
    fetchStats().then(setStats);
  }, []);

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg className="w-8 h-8 animate-spin text-primary-700" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const totalMatchs = stats.totalLocaux;
  const highScoreMatchs = Math.round(totalMatchs * stats.tauxMatching / 100);

  const kpis = [
    {
      id: 'locaux',
      hero: true,
      label: 'Locaux commerciaux actifs',
      value: String(stats.totalLocaux),
      subValue: `${stats.publies + stats.aFortPotentiel} publiés · ${stats.enRevue} en revue`,
      trend: 'up',
      trendValue: `+${stats.totalLocaux} total`,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      color: 'navy',
      breakdown: [
        { label: 'Publiés', value: String(stats.publies + stats.aFortPotentiel), color: 'bg-green-500' },
        { label: 'En revue', value: String(stats.enRevue), color: 'bg-amber-400' },
        { label: 'Brouillons', value: String(stats.brouillons), color: 'bg-white/30' },
      ],
      breakdownWidths: stats.totalLocaux > 0 ? [
        Math.round(((stats.publies + stats.aFortPotentiel) / stats.totalLocaux) * 100),
        Math.round((stats.enRevue / stats.totalLocaux) * 100),
        Math.round((stats.brouillons / stats.totalLocaux) * 100),
      ] : [0, 0, 0],
    },
    {
      id: 'enseignes',
      label: 'Enseignes en recherche',
      value: String(stats.enseignesEnRecherche),
      subValue: `sur ${stats.totalEnseignes} enseignes total`,
      trend: 'up',
      trendValue: `${stats.totalEnseignes} enseignes`,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      color: 'blue',
    },
    {
      id: 'matching',
      label: 'Taux de matching ≥ 70%',
      value: `${stats.tauxMatching}%`,
      subValue: `${highScoreMatchs} matchs sur ${totalMatchs}`,
      trend: 'up',
      trendValue: `${highScoreMatchs} matchs`,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'green',
    },
    {
      id: 'visites',
      label: 'Visites planifiées',
      value: String(stats.visitesPlanifiees),
      subValue: `${stats.visitesRealisees} réalisées · ${stats.totalVisites} total`,
      trend: 'up',
      trendValue: `+${stats.visitesRealisees} réalisées`,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: 'purple',
    },
    {
      id: 'completude',
      label: 'Score de complétude moyen',
      value: `${stats.avgCompletude}%`,
      subValue: 'Objectif : 85%',
      trend: 'neutral',
      trendValue: '↔ stable',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      color: 'amber',
      progressBar: stats.avgCompletude,
    },
    {
      id: 'stale',
      label: 'Locaux sans update +30j',
      value: String(stats.staleLocaux),
      subValue: 'Action requise',
      trend: 'down',
      trendValue: 'Urgent',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'red',
    },
    {
      id: 'docs',
      label: 'Fiches avec docs manquants',
      value: String(stats.locauxSansDocs),
      subValue: `${stats.docsManquants} doc${stats.docsManquants !== 1 ? 's' : ''} manquant${stats.docsManquants !== 1 ? 's' : ''}`,
      trend: 'down',
      trendValue: 'À compléter',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'orange',
    },
    {
      id: 'deals',
      label: 'Deals en négociation',
      value: String(stats.dealsEnNegociation),
      subValue: `${stats.offreRecue} offre${stats.offreRecue !== 1 ? 's' : ''} reçue${stats.offreRecue !== 1 ? 's' : ''}`,
      trend: 'up',
      trendValue: `+${stats.dealsEnNegociation} actifs`,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'emerald',
    },
  ];

  const colorMap: Record<string, { bg: string; icon: string; badge: string; badgeBg: string }> = {
    navy: { bg: 'bg-navy-700', icon: 'text-white', badge: 'text-green-300', badgeBg: 'bg-green-500/20' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', badge: 'text-green-600', badgeBg: 'bg-green-100' },
    green: { bg: 'bg-green-50', icon: 'text-green-600', badge: 'text-green-600', badgeBg: 'bg-green-100' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', badge: 'text-green-600', badgeBg: 'bg-green-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', badge: 'text-amber-600', badgeBg: 'bg-amber-100' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', badge: 'text-red-600', badgeBg: 'bg-red-100' },
    orange: { bg: 'bg-orange-50', icon: 'text-orange-600', badge: 'text-orange-600', badgeBg: 'bg-orange-100' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', badge: 'text-green-600', badgeBg: 'bg-green-100' },
  };

  const hero = kpis[0];
  const rest = kpis.slice(1);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {/* Hero card — spans 2 cols */}
      <div className="col-span-2 bg-navy-700 rounded-2xl p-6 text-white shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-600 text-blue-200 uppercase tracking-widest mb-2">
                {hero.label}
              </p>
              <p className="text-5xl font-700 tabular-nums leading-none">{hero.value}</p>
              <p className="text-sm text-blue-200 mt-2 font-500">{hero.subValue}</p>
            </div>
            <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center text-white">
              {hero.icon}
            </div>
          </div>

          {/* Breakdown bars */}
          <div className="mt-5">
            <div className="flex gap-1 h-2 rounded-full overflow-hidden mb-3">
              {hero.breakdownWidths?.map((w, i) => (
                <div
                  key={i}
                  className={hero.breakdown?.[i]?.color || 'bg-white/30'}
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
            <div className="flex items-center gap-4">
              {hero.breakdown?.map(b => (
                <div key={b.label} className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${b.color}`} />
                  <span className="text-xs text-blue-200">{b.label} <span className="text-white font-600">{b.value}</span></span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-1.5">
            <span className="px-2 py-0.5 bg-green-500/20 text-green-300 text-xs font-600 rounded-full">
              {hero.trendValue}
            </span>
            <span className="text-xs text-blue-300">locaux dans le système</span>
          </div>
        </div>
      </div>

      {/* Regular cards */}
      {rest.map(kpi => {
        const c = colorMap[kpi.color] || colorMap.blue;
        const isAlert = kpi.trend === 'down';
        return (
          <div
            key={kpi.id}
            className={`bg-white rounded-xl border p-5 shadow-card hover:shadow-card-hover transition-shadow duration-200 ${
              isAlert ? 'border-red-200 bg-red-50/30' : 'border-slate-200'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg ${c.bg} flex items-center justify-center ${c.icon}`}>
                {kpi.icon}
              </div>
              {isAlert && (
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              )}
            </div>

            <p className="text-[11px] font-600 text-slate-500 uppercase tracking-wide mb-1 leading-tight">
              {kpi.label}
            </p>
            <p className="text-3xl font-700 text-slate-900 tabular-nums leading-none">{kpi.value}</p>
            <p className="text-xs text-slate-500 mt-1.5 font-500">{kpi.subValue}</p>

            {kpi.progressBar && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-400">Progression</span>
                  <span className="text-xs font-600 text-amber-600">{kpi.progressBar}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-500"
                    style={{ width: `${kpi.progressBar}%` }}
                  />
                </div>
              </div>
            )}

            {!kpi.progressBar && (
              <div className="mt-3 flex items-center gap-1.5">
                <span className={`px-2 py-0.5 text-xs font-600 rounded-full ${c.badgeBg} ${c.badge}`}>
                  {kpi.trendValue}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}